// engine-exception: public quote acceptance page (/q/:token) — unauthenticated, outside the AppShell, driven by a token capability rather than a resource
import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, createClient, type Client } from '../../engine/api/client';
import { EmptyState, Loading } from '../../engine/design/primitives/Misc';
import { QuoteBody, quoteLoadError, type AcceptBody, type PublicView } from './QuoteView';

// The customer's view of a quote (docs/modules/quoting.md §Public acceptance). The URL token is
// the only credential; the page reads the quote, records a view, and hands the document to the
// shared QuoteBody (also used by the customer portal) with the public endpoints.

export type { PublicView } from './QuoteView';

/** The public client: no bearer token, same base URL as the app's API client. */
export function publicClient(): Client {
  return createClient(async () => null, {});
}

export function QuotePage({ client }: { client?: Client } = {}) {
  const { token = '' } = useParams();
  const api = useMemo(() => client ?? publicClient(), [client]);
  const qc = useQueryClient();
  const key = ['public-quote', token];
  const path = `/public/quotes/${encodeURIComponent(token)}`;
  const view = useQuery<PublicView, ApiError>({ queryKey: key, queryFn: () => api.request<PublicView>(path), retry: false, enabled: Boolean(token) });
  // Record the view once per page load (best effort; the server rates it to hourly per link).
  useEffect(() => {
    if (!token || !view.data || view.data.quote.status !== 'sent') return;
    void api.request<PublicView>(`${path}/view`, { method: 'POST', body: {} }).then((v) => qc.setQueryData(key, v)).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, view.data?.quote.id]);

  if (!token) return <div className="hf-public"><EmptyState title="No quote link" icon="quote" /></div>;
  if (view.isLoading) return <div className="hf-public"><Loading label="Loading your quote…" /></div>;
  if (view.error) {
    const { title, hint, icon } = quoteLoadError(view.error);
    return <div className="hf-public"><EmptyState title={title} hint={hint} icon={icon} /></div>;
  }
  const endpoints = {
    accept: (body: AcceptBody) => api.request<PublicView>(`${path}/accept`, { method: 'POST', body }),
    decline: (body: { reason: string | null }) => api.request<PublicView>(`${path}/decline`, { method: 'POST', body }),
    printUrl: `${api.baseUrl}${path}/print`,
  };
  return <QuoteBody endpoints={endpoints} data={view.data!} onChange={(v) => qc.setQueryData(key, v)} />;
}
